import axios from "axios";

// Get the API URL from environment variables
const baseUrl = process.env.REACT_APP_API_URL || "";

/**
 * Axios instance for making API requests.
 * Configured with a base URL and a timeout.
 * @type {import("axios").AxiosInstance}
 */
const webApiClient = axios.create({
    baseURL: baseUrl,
    timeout: 10000,
});

/**
 * Request interceptor to automatically add the Authorization header with the access token
 * to outgoing requests. It also adds development mode headers when enabled.
 */
// Add request interceptor for auth token and development mode headers
webApiClient.interceptors.request.use(
    async config => {
        const token = localStorage.getItem("accessToken"); // Change from "authToken" to "accessToken"
        
        // Add window function to clear tokens for debugging
        if (typeof window !== 'undefined') {
            window.clearAuthTokens = () => {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                console.log('Cleared all auth tokens. Please refresh and login again.');
            };
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            config.headers["ngrok-skip-browser-warning"] = "true";
        }
        config.headers["ngrok-skip-browser-warning"] = "true";

        // Auto-detect environment: localhost OR devMode URL parameter OR localStorage = dev database
        const IS_LOCALHOST = window.location.hostname === 'localhost';
        const urlParams = new URLSearchParams(window.location.search);
        const devModeFromUrl = urlParams.get('devMode') === 'true';
        const devModeFromStorage = localStorage.getItem('dance-locale-dev-mode') === 'true';

        if (IS_LOCALHOST || devModeFromUrl || devModeFromStorage) {
            config.headers['X-App-Version'] = 'development-app';
        }

        return config;
    },
    error => {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
    }
);

/**
 * Response interceptor that automatically refreshes expired access tokens and handles error formatting.
 * When a 401 error is received, attempts to refresh the access token using the stored refresh token.
 * If refresh succeeds, retries the original request with the new token.
 * Also normalizes all error responses into a consistent format { ok, data, status, problem }.
 * @param {object} response - The successful response object (passed through unchanged)
 * @param {object} error - The error object from failed requests
 * @returns {Promise<object>} The response object for successful requests, or formatted error object for failures
 */
// Add automatic token refresh interceptor with integrated error handling
webApiClient.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const refreshToken = localStorage.getItem("refreshToken");

            if (refreshToken) {
                try {
                    // Build headers with dev mode support
                    const refreshHeaders = {
                        "Content-Type": "application/json",
                        "ngrok-skip-browser-warning": "true",
                    };

                    // CRITICAL: Add dev header for token refresh to use correct database (auto-detect environment)
                    const IS_LOCALHOST = window.location.hostname === 'localhost';
                    const urlParams = new URLSearchParams(window.location.search);
                    const devModeFromUrl = urlParams.get('devMode') === 'true';
                    const devModeFromStorage = localStorage.getItem('dance-locale-dev-mode') === 'true';

                    if (IS_LOCALHOST || devModeFromUrl || devModeFromStorage) {
                        refreshHeaders['X-App-Version'] = 'development-app';
                    }

                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/auth/refresh`, {
                        method: "POST",
                        headers: refreshHeaders,
                        body: JSON.stringify({ refreshToken }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        localStorage.setItem("accessToken", data.accessToken);

                        // Update the authorization header
                        originalRequest.headers["Authorization"] = `Bearer ${data.accessToken}`;

                        // CRITICAL FIX: Ensure URL is relative to baseURL for proper retry
                        // Sometimes axios stores absolute URL in originalRequest.url which breaks retry
                        // Extract path from absolute URL if needed
                        if (originalRequest.url.startsWith('http')) {
                            try {
                                const url = new URL(originalRequest.url);
                                originalRequest.url = url.pathname + url.search;
                            } catch (e) {
                                console.error("Failed to parse URL for token refresh retry:", originalRequest.url);
                            }
                        }

                        return webApiClient(originalRequest);
                    }
                } catch (refreshError) {
                    console.error("Token refresh failed:", refreshError);
                }
            }

            // Clear tokens and flag session expiration for user message
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.setItem("sessionExpired", "true");

            // Reload page to trigger redirect to login with message
            window.location.reload();
        }

        // Integrated error handling (moved from second interceptor)
        let errorResponse = {
            ok: false,
            data: null,
            status: 500,
            problem: "UNKNOWN_ERROR",
        };

        if (error.response) {
            // Flatten RFC 7807 error format from backend for easier frontend access
            // Backend sends: { error: { code, message, retryable, details } }
            // We flatten to: { message, code, retryable, details }
            const backendError = error.response.data?.error;

            if (backendError && typeof backendError === "object") {
                // RFC 7807 format - flatten it. Some manual controller replies put only the
                // code in `error` and the message beside it ({ message, error: { code } }).
                errorResponse.data = {
                    message: backendError.message || error.response.data?.message || "An error occurred",
                    code: backendError.code || error.response.data?.errorCode,
                    retryable: backendError.retryable,
                    ...(backendError.details && { details: backendError.details })
                };
            } else if (typeof backendError === "string") {
                // Manual reply shape { error: <internal string>, errorCode, userMessage }:
                // surface the user-facing message and the code.
                errorResponse.data = {
                    ...error.response.data,
                    message: error.response.data.userMessage || error.response.data.message || backendError,
                    code: error.response.data.errorCode,
                };
            } else {
                // Legacy format or non-standard response - pass through
                errorResponse.data = error.response.data;
            }

            errorResponse.status = error.response.status;
            errorResponse.problem = "CLIENT_ERROR";
        } else if (error.request) {
            errorResponse.problem = "NETWORK_ERROR";
            errorResponse.data = {
                message: `Cannot connect to server at ${baseUrl}. Please check your network connection and server status.`,
            };
        } else {
            errorResponse.problem = "CONNECTIVITY_ERROR";
            errorResponse.data = {
                message: `An error occurred: ${error.message}`,
            };
        }

        if (error.code === "ECONNABORTED") {
            errorResponse.problem = "TIMEOUT_ERROR";
            errorResponse.data = {
                message: `Request timed out. Server at ${baseUrl} is not responding.`,
            };
        }

        return Promise.reject(errorResponse);
    }
);

/**
 * A client object that provides methods mimicking the apisauce API for making HTTP requests.
 * It uses the configured `webApiClient` (axios instance) internally.
 * Successful responses are wrapped with `{ ok: true, ...response }`.
 * Failed responses (catches) are returned as is (which will be the error object from the error interceptor).
 */
const compatClient = {
    // Basic HTTP methods
    /**
     * Performs a GET request.
     * @param {string} url - The URL to request.
     * @param {object} [params] - The URL parameters to be sent with the request.
     * @returns {Promise<object>} A promise that resolves to the response object or rejects with an error object.
     */
    get: (url, params, options = {}) => {
        const config = { params };
        // Override baseURL if useDev flag is set
        if (options.useDev) {
            config.baseURL = process.env.REACT_APP_DEV_API_URL;
        }
        // Allow per-request timeout override (e.g. for image fetches on slow mobile connections)
        if (options.timeout) {
            config.timeout = options.timeout;
        }
        return webApiClient
            .get(url, config)
            .then(r => ({ ok: true, ...r }))
            .catch(e => e);
    },
    /**
     * Performs a POST request.
     * @param {string} url - The URL to request.
     * @param {object} [data] - The data to be sent as the request body.
     * @param {import("axios").AxiosRequestConfig} [config] - Axios request configuration.
     * @returns {Promise<object>} A promise that resolves to the response object or rejects with an error object.
     */
    post: (url, data, options = {}) => {
        const config = {};
        if (options.timeout) config.timeout = options.timeout;
        return webApiClient
            .post(url, data, config)
            .then(r => ({ ok: true, ...r }))
            .catch(e => e);
    },
    /**
     * Performs a PUT request.
     * @param {string} url - The URL to request.
     * @param {object} [data] - The data to be sent as the request body.
     * @param {object} [options] - Options: { timeout } to override the default request timeout.
     * @returns {Promise<object>} A promise that resolves to the response object or rejects with an error object.
     */
    put: (url, data, options = {}) => {
        const config = {};
        if (options.timeout) config.timeout = options.timeout;
        return webApiClient
            .put(url, data, config)
            .then(r => ({ ok: true, ...r }))
            .catch(e => e);
    },
    /**
     * Performs a PATCH request.
     * @param {string} url - The URL to request.
     * @param {object} [data] - The data to be sent as the request body.
     * @param {object} [options] - Options: { timeout } to override the default request timeout.
     * @returns {Promise<object>} A promise that resolves to the response object or rejects with an error object.
     */
    patch: (url, data, options = {}) => {
        const config = {};
        if (options.timeout) config.timeout = options.timeout;
        return webApiClient
            .patch(url, data, config)
            .then(r => ({ ok: true, ...r }))
            .catch(e => e);
    },
    /**
     * Performs a DELETE request.
     * @param {string} url - The URL to request.
     * @param {object} [options] - Options: { timeout } to override the default request timeout.
     * @returns {Promise<object>} A promise that resolves to the response object or rejects with an error object.
     */
    delete: (url, options = {}) => {
        const config = {};
        if (options.timeout) config.timeout = options.timeout;
        return webApiClient
            .delete(url, config)
            .then(r => ({ ok: true, ...r }))
            .catch(e => e);
    },

    /** @type {() => void} No-op function, as transforms are handled by axios interceptors. */
    addRequestTransform: () => {},
    /** @type {() => void} No-op function, as transforms are handled by axios interceptors. */
    addResponseTransform: () => {},
    /** @type {() => void} No-op function, as transforms are handled by axios interceptors. */
    addAsyncRequestTransform: () => {},
    /** @type {() => void} No-op function, as transforms are handled by axios interceptors. */
    addAsyncResponseTransform: () => {},
};

export default compatClient;
